import { documentTemplateApi } from "./axios";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { DocumentTemplateI, ServiceResponseI } from "../../interfaces";
import { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";

const handleErr = (err: unknown): never => {
  const obj = (err as AxiosError).response;
  if (obj?.status === 401) LogoutSystem();
  throw new Error((obj?.data as Error)?.message ?? "Error desconocido");
};

export const listTemplates = async (
  typeCode?: string,
  scopeCode?: string,
): Promise<ServiceResponseI<DocumentTemplateI[]>> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const params: Record<string, string> = {};
    if (typeCode) params.typeCode = typeCode;
    if (scopeCode) params.scopeCode = scopeCode;
    const { data } = await documentTemplateApi.get("", {
      params,
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const uploadSystemTemplate = async (
  file: File,
  typeCode: string,
  nombre: string,
  scopeCode: string,
) => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");
  const form = new FormData();
  form.append("file", file);
  form.append("typeCode", typeCode);
  form.append("nombre", nombre);
  form.append("scopeCode", scopeCode);
  const { data } = await documentTemplateApi.post("/system", form, {
    headers: { Authorization: token }, // sin Content-Type, axios genera el boundary
  });
  return data;
};

export const uploadTemplate = async (
  file: File,
  typeCode: string,
  nombre: string,
  scopeCode: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const form = new FormData();
    form.append("file", file);
    form.append("typeCode", typeCode);
    form.append("nombre", nombre);
    form.append("scopeCode", scopeCode);
    const { data } = await documentTemplateApi.post("", form, {
      headers: { Authorization: token }, // quité el "Content-Type": "multipart/form-data" que tenías
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const downloadTemplate = async (
  id: string,
  nombre: string,
): Promise<void> => {
  const token = ValidateCurrentToken();
  if (!token) throw new Error("TOKEN INVALIDO");
  try {
    const response = await documentTemplateApi.get(`/${id}/download`, {
      headers: { Authorization: token },
      responseType: "blob",
    });
    const disposition = response.headers["content-disposition"] as
      | string
      | undefined;
    const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const filename = match ? decodeURIComponent(match[1]) : `${nombre}.docx`;

    const url = window.URL.createObjectURL(response.data as Blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    const status = (err as AxiosError).response?.status;
    if (status === 401) LogoutSystem();
    throw new Error("No se pudo descargar la plantilla");
  }
};

export const setDefaultTemplate = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await documentTemplateApi.patch(
      `/${id}/default`,
      {},
      { headers: { Authorization: token } },
    );
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const deleteTemplate = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await documentTemplateApi.delete(`/${id}`, {
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const useDocumentTemplates = (typeCode?: string, scopeCode?: string) => {
  const templatesQuery = useQuery(
    ["document-templates", typeCode, scopeCode],
    () => listTemplates(typeCode, scopeCode),
    { refetchOnWindowFocus: false, keepPreviousData: true },
  );
  return { templatesQuery };
};